var __esDecorate = (this && this.__esDecorate) || function (ctor, descriptorIn, decorators, contextIn, initializers, extraInitializers) {
    function accept(f) { if (f !== void 0 && typeof f !== "function") throw new TypeError("Function expected"); return f; }
    var kind = contextIn.kind, key = kind === "getter" ? "get" : kind === "setter" ? "set" : "value";
    var target = !descriptorIn && ctor ? contextIn["static"] ? ctor : ctor.prototype : null;
    var descriptor = descriptorIn || (target ? Object.getOwnPropertyDescriptor(target, contextIn.name) : {});
    var _, done = false;
    for (var i = decorators.length - 1; i >= 0; i--) {
        var context = {};
        for (var p in contextIn) context[p] = p === "access" ? {} : contextIn[p];
        for (var p in contextIn.access) context.access[p] = contextIn.access[p];
        context.addInitializer = function (f) { if (done) throw new TypeError("Cannot add initializers after decoration has completed"); extraInitializers.push(accept(f || null)); };
        var result = (0, decorators[i])(kind === "accessor" ? { get: descriptor.get, set: descriptor.set } : descriptor[key], context);
        if (kind === "accessor") {
            if (result === void 0) continue;
            if (result === null || typeof result !== "object") throw new TypeError("Object expected");
            if (_ = accept(result.get)) descriptor.get = _;
            if (_ = accept(result.set)) descriptor.set = _;
            if (_ = accept(result.init)) initializers.unshift(_);
        }
        else if (_ = accept(result)) {
            if (kind === "field") initializers.unshift(_);
            else descriptor[key] = _;
        }
    }
    if (target) Object.defineProperty(target, contextIn.name, descriptor);
    done = true;
};
var __runInitializers = (this && this.__runInitializers) || function (thisArg, initializers, value) {
    var useValue = arguments.length > 2;
    for (var i = 0; i < initializers.length; i++) {
        value = useValue ? initializers[i].call(thisArg, value) : initializers[i].call(thisArg);
    }
    return useValue ? value : void 0;
};
var __setFunctionName = (this && this.__setFunctionName) || function (f, name, prefix) {
    if (typeof name === "symbol") name = name.description ? "[".concat(name.description, "]") : "";
    return Object.defineProperty(f, "name", { configurable: true, value: prefix ? "".concat(prefix, " ", name) : name });
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, state, value, kind, f) {
    if (kind === "m") throw new TypeError("Private method is not writable");
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a setter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot write private member to an object whose class did not declare it");
    return (kind === "a" ? f.call(receiver, value) : f ? f.value = value : state.set(receiver, value)), value;
};
import { html, LitElement } from 'lit';
import styles from './sds-skip-to-content.lit.scss';
import { sdsCustomElement } from '../utilities/custom-components';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit-html/directives/if-defined.js';
/**
 * `sds-skip-to-content` is a component that provides a link or button allowing users to bypass repetitive navigation elements and jump directly to the main content of the page. This enhances accessibility by enabling keyboard and screen reader users to navigate more efficiently.
 *
 * ### Attributes
 * - `href`: A string specifying the target ID of the main content section with hash (e.g., "#main-content"). This will render as a link and change the URL.
 * - `target-id`: A string specifying the target ID of the main content section without hash (e.g., "main-content"). This will render as a button and move focus without changing the URL.
 *
 * ### Example Usage
 * ```html
 * <!-- Link behavior (changes URL) -->
 * <sds-skip-to-content href="#main-content">Skip to main content</sds-skip-to-content>
 *
 * <!-- Button behavior (focus only, no URL change) -->
 * <sds-skip-to-content target-id="main-content">Skip to main content</sds-skip-to-content>
 * ```
 *
 * @tag sds-skip-to-content
 *
 * @attr {string} href - A string specifying the target ID with hash for link behavior.
 * @attr {string} target-id - A string specifying the target ID without hash for button behavior.
 */
let SdsSkipToContent = (() => {
    var _SdsSkipToContent_href_accessor_storage, _SdsSkipToContent_targetId_accessor_storage, _SdsSkipToContent_label_accessor_storage;
    let _classDecorators = [sdsCustomElement('sds-skip-to-content')];
    let _classDescriptor;
    let _classExtraInitializers = [];
    let _classThis;
    let _classSuper = LitElement;
    let _href_decorators;
    let _href_initializers = [];
    let _href_extraInitializers = [];
    let _targetId_decorators;
    let _targetId_initializers = [];
    let _targetId_extraInitializers = [];
    let _label_decorators;
    let _label_initializers = [];
    let _label_extraInitializers = [];
    var SdsSkipToContent = _classThis = class extends _classSuper {
        constructor() {
            super(...arguments);
            _SdsSkipToContent_href_accessor_storage.set(this, __runInitializers(this, _href_initializers, void 0));
            _SdsSkipToContent_targetId_accessor_storage.set(this, (__runInitializers(this, _href_extraInitializers), __runInitializers(this, _targetId_initializers, void 0)));
            _SdsSkipToContent_label_accessor_storage.set(this, (__runInitializers(this, _targetId_extraInitializers), __runInitializers(this, _label_initializers, 'Skip to main content')));
            this.handleClick = (__runInitializers(this, _label_extraInitializers), (event) => {
                event.preventDefault();
                if (this.targetId) {
                    const targetElement = document.getElementById(this.targetId);
                    if (targetElement) {
                        // Check if the element is naturally focusable
                        const isNaturallyFocusable = targetElement.matches('button, input, select, textarea, a[href], [tabindex]:not([tabindex="-1"])');
                        // Only add tabindex if the element is not naturally focusable
                        if (!isNaturallyFocusable && !targetElement.hasAttribute('tabindex')) {
                            targetElement.setAttribute('tabindex', '-1');
                        }
                        // Focus the target element
                        targetElement.focus();
                    }
                }
            });
        }
        /**
         * Href of skip-to-content (for link behavior)
         * @attr
         */
        get href() { return __classPrivateFieldGet(this, _SdsSkipToContent_href_accessor_storage, "f"); }
        set href(value) { __classPrivateFieldSet(this, _SdsSkipToContent_href_accessor_storage, value, "f"); }
        /**
         * Target ID of skip-to-content (for button behavior)
         * @attr
         */
        get targetId() { return __classPrivateFieldGet(this, _SdsSkipToContent_targetId_accessor_storage, "f"); }
        set targetId(value) { __classPrivateFieldSet(this, _SdsSkipToContent_targetId_accessor_storage, value, "f"); }
        /**
         * Label of skip-to-content
         * @attr
         */
        get label() { return __classPrivateFieldGet(this, _SdsSkipToContent_label_accessor_storage, "f"); }
        set label(value) { __classPrivateFieldSet(this, _SdsSkipToContent_label_accessor_storage, value, "f"); }
        connectedCallback() {
            super.connectedCallback();
        }
        disconnectedCallback() {
            super.disconnectedCallback();
        }
        /**
         * Focuses the skip-to-content button
         */
        focus() {
            var _a;
            const button = (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector('.sds-skip-to-content');
            if (button) {
                button.focus();
            }
        }
        render() {
            // If href is provided, render as a link
            if (this.href) {
                return html `
                <a class="sds-skip-to-content" href="${ifDefined(this.href)}"> ${this.label} </a>
            `;
            }
            // If target-id is provided, render as a button
            if (this.targetId) {
                return html `
                <button class="sds-skip-to-content" @click="${this.handleClick}" type="button">
                    ${this.label}
                </button>
            `;
            }
            // Fallback to button if neither is provided
            return html ` <button class="sds-skip-to-content" type="button">${this.label}</button> `;
        }
    };
    _SdsSkipToContent_href_accessor_storage = new WeakMap();
    _SdsSkipToContent_targetId_accessor_storage = new WeakMap();
    _SdsSkipToContent_label_accessor_storage = new WeakMap();
    __setFunctionName(_classThis, "SdsSkipToContent");
    (() => {
        var _a;
        const _metadata = typeof Symbol === "function" && Symbol.metadata ? Object.create((_a = _classSuper[Symbol.metadata]) !== null && _a !== void 0 ? _a : null) : void 0;
        _href_decorators = [property({ type: String })];
        _targetId_decorators = [property({ type: String, attribute: 'target-id' })];
        _label_decorators = [property({ type: String })];
        __esDecorate(_classThis, null, _href_decorators, { kind: "accessor", name: "href", static: false, private: false, access: { has: obj => "href" in obj, get: obj => obj.href, set: (obj, value) => { obj.href = value; } }, metadata: _metadata }, _href_initializers, _href_extraInitializers);
        __esDecorate(_classThis, null, _targetId_decorators, { kind: "accessor", name: "targetId", static: false, private: false, access: { has: obj => "targetId" in obj, get: obj => obj.targetId, set: (obj, value) => { obj.targetId = value; } }, metadata: _metadata }, _targetId_initializers, _targetId_extraInitializers);
        __esDecorate(_classThis, null, _label_decorators, { kind: "accessor", name: "label", static: false, private: false, access: { has: obj => "label" in obj, get: obj => obj.label, set: (obj, value) => { obj.label = value; } }, metadata: _metadata }, _label_initializers, _label_extraInitializers);
        __esDecorate(null, _classDescriptor = { value: _classThis }, _classDecorators, { kind: "class", name: _classThis.name, metadata: _metadata }, null, _classExtraInitializers);
        SdsSkipToContent = _classThis = _classDescriptor.value;
        if (_metadata) Object.defineProperty(_classThis, Symbol.metadata, { enumerable: true, configurable: true, writable: true, value: _metadata });
    })();
    _classThis.styles = styles;
    (() => {
        __runInitializers(_classThis, _classExtraInitializers);
    })();
    return SdsSkipToContent = _classThis;
})();
export { SdsSkipToContent };
